import React, { useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';

import { Tooltip } from 'antd';
import MergeClassNames from 'utils/MergeClassNames';

interface ChipProps {
  id: string;
  label: string;
  selected?: boolean;
  onClose?: (id: string) => void;
  onClick?: (id: string) => void;
  /**
   * When true, the label is capped at ~22rem (matching the Target picker's
   * dropdown width) and truncated with an ellipsis. An AntD Tooltip showing
   * the full text appears on hover only when the label is actually clipped.
   */
  truncate?: boolean;
  /** `filled` reads as a value rather than a button: a white tag without an outline, for a grey ground (Target tags, OP-2556). */
  variant?: 'outline' | 'filled';
  /** Extra classes, e.g. `max-w-full` to truncate within a narrow cell. */
  className?: string;
}

const Chip: React.FC<ChipProps> = ({
  id,
  label,
  selected = false,
  onClose,
  onClick,
  truncate = false,
  variant = 'outline',
  className,
}) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    const el = labelRef.current;
    setShowTooltip(!!el && el.scrollWidth > el.clientWidth);
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClick?.(id);
  };

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? -1 : undefined}
      onClick={onClick ? handleClick : undefined}
      className={MergeClassNames(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium leading-4',
        truncate && 'max-w-[22rem]',
        onClick && 'cursor-pointer hover:border-gray-dark',
        selected
          ? 'border-success-dark bg-success-dark text-white'
          : variant === 'filled'
            ? 'border-transparent bg-white text-sm font-semibold text-charcoal'
            : 'border-gray-argent bg-white text-gray-dark',
        className
      )}
    >
      {truncate ? (
        <Tooltip title={label} open={showTooltip}>
          <span
            ref={labelRef}
            className="min-w-0 truncate"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {label}
          </span>
        </Tooltip>
      ) : (
        label
      )}
      {onClose && (
        <button
          type="button"
          className="flex items-center justify-center rounded-full text-current hover:opacity-70"
          onClick={(event) => {
            if (onClick) event.stopPropagation();
            onClose(id);
          }}
          aria-label={`Remove ${label}`}
        >
          <IoClose size={12} />
        </button>
      )}
    </span>
  );
};

export default Chip;
